import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { TFacilityMembership } from "api/rpc/2024-04/facilityAdmin/membership/membership";

import Card from "components/card/Card";
import DataTable from "components/dataTable/DataTable";
import Input from "components/form/input/Input";
import { ButtonNew } from "components/buttonNew";

import AttachMembershipModal from "./AttachMembershipModal";

export type TPendingIncludedMembership = {
  id?: number;
  membership_id: number;
  quantity: number;
  membership?: Pick<TFacilityMembership, "id" | "title" | "type" | "subtitle">;
};

type IncludedMembershipsCardProps = {
  included: TPendingIncludedMembership[];
  onChange: (included: TPendingIncludedMembership[]) => void;
  disabled?: boolean;
  userLevel?: "facility" | "client";
};

export default function IncludedMembershipsCard(props: IncludedMembershipsCardProps) {
  const { t } = useTranslation();
  const [attachOpen, setAttachOpen] = useState(false);
  const [quantityDrafts, setQuantityDrafts] = useState<Record<number, string>>({});

  function handleAttach(membership: TFacilityMembership) {
    if (props.included.some(row => row.membership_id === membership.id)) {
      setAttachOpen(false);
      return;
    }

    props.onChange([...props.included, { membership_id: membership.id, quantity: 1, membership }]);
    setAttachOpen(false);
  }

  function parseQuantity(value: string) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return null;
    }

    return Math.floor(parsed);
  }

  function handleQuantityChange(row: TPendingIncludedMembership, value: string) {
    setQuantityDrafts(prev => ({ ...prev, [row.membership_id]: value }));

    const nextQuantity = parseQuantity(value);
    if (nextQuantity === null || nextQuantity === row.quantity) {
      return;
    }

    props.onChange(
      props.included.map(item =>
        item.membership_id === row.membership_id ? { ...item, quantity: nextQuantity } : item,
      ),
    );
  }

  function handleQuantityBlur(row: TPendingIncludedMembership) {
    const draft = quantityDrafts[row.membership_id];
    setQuantityDrafts(prev => {
      const next = { ...prev };
      delete next[row.membership_id];
      return next;
    });

    if (draft === undefined) {
      return;
    }

    const nextQuantity = parseQuantity(draft) ?? 1;
    if (nextQuantity === row.quantity) {
      return;
    }

    props.onChange(
      props.included.map(item =>
        item.membership_id === row.membership_id ? { ...item, quantity: nextQuantity } : item,
      ),
    );
  }

  function handleRemove(row: TPendingIncludedMembership) {
    setQuantityDrafts(prev => {
      const next = { ...prev };
      delete next[row.membership_id];
      return next;
    });
    props.onChange(props.included.filter(item => item.membership_id !== row.membership_id));
  }

  return (
    <>
      <Card
        title={t("Included Memberships")}
        subtitle={t("Choose which memberships this group sale applies, and how many people receive each one.")}
        titleActions={[
          {
            action: () => setAttachOpen(true),
            content: (
              <>
                <FontAwesomeIcon size="lg" className="mr-2" icon={faPlus} />
                {t("Attach membership")}
              </>
            ),
            buttonType: "text",
            disabled: props.disabled,
          },
        ]}
      >
        <Card.Section>
          <DataTable columns={[{ label: t("Membership") }, { label: t("Quantity") }, { label: t("Actions") }]}>
            {props.included.length === 0 ? (
              <tr>
                <td colSpan={3}>{t("No included memberships")}</td>
              </tr>
            ) : (
              props.included.map(row => (
                <tr key={row.membership_id}>
                  <td>{row.membership?.title ?? row.membership_id}</td>
                  <td>
                    <Input
                      type="number"
                      value={quantityDrafts[row.membership_id] ?? String(row.quantity)}
                      onChange={event => handleQuantityChange(row, event.target.value)}
                      onBlur={() => handleQuantityBlur(row)}
                      onKeyDown={event => {
                        if (event.key === "Enter") {
                          event.currentTarget.blur();
                        }
                      }}
                      disabled={props.disabled}
                    />
                  </td>
                  <td>
                    <ButtonNew type="secondary" size="small" onClick={() => handleRemove(row)} disabled={props.disabled}>
                      {t("Remove")}
                    </ButtonNew>
                  </td>
                </tr>
              ))
            )}
          </DataTable>
        </Card.Section>
      </Card>
      <AttachMembershipModal
        isVisible={attachOpen}
        onCancel={() => setAttachOpen(false)}
        onOk={membership => handleAttach(membership)}
        excludedMembershipIds={props.included.map(row => row.membership_id)}
        userLevel={props.userLevel}
      />
    </>
  );
}
